import type { MockEdge, MockNode } from './types'

/**
 * Which nodes cytoscape's `breadthfirst` layout should treat as tops.
 *
 * Kept out of the canvas component so it is unit-testable and the component
 * exports only a component — the same reason matchTone.ts / boardLayout.ts /
 * filters.ts sit beside theirs.
 *
 * Two graph shapes reach this, and they disagree about what a root is:
 *
 * - **Edge-based** (the tag drill-down: site → sheet → tag). Nothing sets
 *   `parent`, so a rule keyed on a missing `parent` marks EVERY node a root and
 *   the layout renders one flat row.
 * - **Compound** (the Knowledge Graph page's hierarchy). Containment is in
 *   `parent`, so a rule keyed only on incoming edges marks every node a root
 *   whenever the graph carries no edges.
 *
 * A top therefore has to satisfy both: no incoming edge AND no parent. The two
 * fallbacks cover the shapes where that is empty — a fully cyclic graph (every
 * node has an incoming edge), and then the degenerate case — because handing
 * the layout an empty root set lets it choose arbitrarily.
 */
export const computeRootIds = (nodes: MockNode[], edges: MockEdge[]): string[] => {
  const hasIncoming = new Set(edges.map((e) => e.target))

  const tops = nodes.filter((n) => !hasIncoming.has(n.id) && !n.parent).map((n) => n.id)
  if (tops.length > 0) return tops

  const parentless = nodes.filter((n) => !n.parent).map((n) => n.id)
  if (parentless.length > 0) return parentless

  return nodes.map((n) => n.id)
}
